import { useState, useEffect } from 'react';
// @mui
import { styled } from '@mui/material/styles';
import { Input, Slide, IconButton, InputAdornment, ClickAwayListener, Stack, Typography } from '@mui/material';
// component
import Iconify from '../../../components/iconify';
import Handrail from 'src/pages/Handrail/Handrail';
import Airports from 'src/pages/Airports/Airports';
import Offers from 'src/pages/Offers/Offers';
import DocumentationBteeSa from 'src/pages/DocumentationBteeSa/DocumentationBteeSa';
import Awhm from 'src/pages/AWHM-ABM/Awhm/Awhm';
import Abm from 'src/pages/AWHM-ABM/ABM/Abm';
import Tasks from 'src/pages/Tasks/Tasks';
import Trainees from 'src/pages/Airtrace/Trainees/Trainees';
import Courses from 'src/pages/Airtrace/Courses/Courses';
import Trainings from 'src/pages/Airtrace/Trainings/Trainings';
import Users from 'src/pages/Users/Users';
import i18n from 'src/i18n';
import { useLocation } from 'react-router-dom';

// ----------------------------------------------------------------------

const StyledSearchbar = styled('div')(({ theme }) => ({
  // ...bgBlur({ color: theme.palette.background.default }),
  backgroundColor: 'white',
  opacity: 0.95,
  top: '50%',
  left: '5%',
  transform: 'translate(-30%, -50%)',
  zIndex: 99,
  width: '90%',
  margin: 'auto',
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  // height: HEADER_MOBILE,
  padding: theme.spacing(2, 3),
  boxShadow: theme.customShadows.z8,
  [theme.breakpoints.up('md')]: {
    width: '70%',
    // height: HEADER_DESKTOP,
    left: `calc(20% + 40px)`,
    padding: theme.spacing(2, 5),
  },
}));

// ----------------------------------------------------------------------

export default function Searchbar() {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Debounce logic
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300); // 300ms debounce time

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const handleOpen = () => {
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div>
        {!open && (
          <IconButton onClick={handleOpen}>
            <Iconify icon="eva:search-fill" />
          </IconButton>
        )}

        <Slide direction="down" in={open} mountOnEnter unmountOnExit style={{ borderRadius: '5px' }}>
          <StyledSearchbar>
            <Stack direction="column" sx={{ width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
              <Stack direction="row" sx={{ width: '100%' }}>
                <Input
                  autoFocus
                  fullWidth
                  disableUnderline
                  placeholder="Search…"
                  startAdornment={
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                    </InputAdornment>
                  }
                  sx={{ mr: 1, fontWeight: 'fontWeightBold' }}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </Stack>

              {(!debouncedQuery || debouncedQuery.trim() === '') ? (
                <Stack direction="row" spacing={2} alignItems={'center'} justifyContent={'center'} sx={{ mt: 5 }}>
                  <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                    {i18n.t("start_typing_to_search_across_all_modules")}
                  </Typography>
                </Stack>
              ) : (
                <>
                  <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Airports
                      showTitle={true}
                      limit={4}
                      hideActionsBar={true}
                      hidePagination={true}
                      search={debouncedQuery}
                      hideIfNoResults={true}
                    />
                  </Stack>

                  <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Offers
                      showTitle={true}
                      limit={4}
                      hideActionsBar={true}
                      hidePagination={true}
                      search={debouncedQuery}
                      hideIfNoResults={true}
                    />
                  </Stack>

                  <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Handrail
                      showTitle={true}
                      limit={4}
                      hideActionsBar={true}
                      hidePagination={true}
                      search={debouncedQuery}
                      hideIfNoResults={true}
                    />
                  </Stack>

                  <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <DocumentationBteeSa
                      showTitle={true}
                      limit={4}
                      hideActionsBar={true}
                      hidePagination={true}
                      search={debouncedQuery}
                      hideIfNoResults={true}
                    />
                  </Stack>

                  <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Awhm
                      showTitle={true}
                      limit={4}
                      hideActionsBar={true}
                      hidePagination={true}
                      search={debouncedQuery}
                      hideIfNoResults={true}
                    />
                  </Stack>

                  <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Abm
                      showTitle={true}
                      limit={4}
                      hideActionsBar={true}
                      hidePagination={true}
                      search={debouncedQuery}
                      hideIfNoResults={true}
                    />
                  </Stack>

                  <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Tasks
                      showTitle={true}
                      limit={4}
                      hideActionsBar={true}
                      hidePagination={true}
                      search={debouncedQuery}
                      hideIfNoResults={true}
                    />
                  </Stack>

                  <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Trainees
                      showTitle={true}
                      limit={4}
                      hideActionsBar={true}
                      hidePagination={true}
                      search={debouncedQuery}
                      hideIfNoResults={true}
                    />
                  </Stack>

                  <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Courses
                      showTitle={true}
                      limit={4}
                      hideActionsBar={true}
                      hidePagination={true}
                      search={debouncedQuery}
                      hideIfNoResults={true}
                    />
                  </Stack>

                  <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Trainings
                      showTitle={true}
                      limit={4}
                      hideActionsBar={true}
                      hidePagination={true}
                      search={debouncedQuery}
                      hideIfNoResults={true}
                    />
                  </Stack>

                  <Stack direction="row" spacing={2} sx={{ mt: 2, mb: 2 }}>
                    <Users
                      showTitle={true}
                      limit={4}
                      hideActionsBar={true}
                      hidePagination={true}
                      search={debouncedQuery}
                      hideIfNoResults={true}
                    />
                  </Stack>
                </>
              )}

            </Stack>

          </StyledSearchbar>
        </Slide>
      </div>
    </ClickAwayListener>
  );
}
